<script>
  export default {
    computed: {
      yearDate() {
        let d = new Date
        return d.getFullYear();
      }
    },
    name: 'FooterComponent'
  }
</script>

<template>
    <!-- Footer -->
    <footer class="bg-gradient-to-br from-dark via-primary to-secondary relative overflow-hidden">
      <!-- Background Pattern -->
      <div class="absolute inset-0 opacity-5">
        <div class="absolute inset-0" style="background-image: radial-gradient(circle at 2px 2px, white 1px, transparent 0); background-size: 40px 40px;"></div>
      </div>
      
      <!-- Main Footer Content -->
      <div class="relative z-10 py-20">
        <div class="container mx-auto px-4">
          <!-- Top Section -->
          <div class="grid grid-cols-1 lg:grid-cols-4 gap-12 mb-16">
            <!-- Brand Column -->
            <div class="lg:col-span-2">
              <div class="mb-8">
                <h2 class="text-4xl font-bold font-heading text-white mb-4">DIGINOVA</h2>
                <p class="text-white/80 text-lg leading-relaxed max-w-md">
                  Blending digital and reality. We create stunning experiences that bring your vision to life.
                </p>
              </div>
              
              <!-- Contact Info -->
              <div class="space-y-4">
                <div class="flex items-center space-x-3">
                  <div class="w-10 h-10 bg-accent rounded-full flex items-center justify-center">
                    <i class="fas fa-envelope text-dark"></i>
                  </div>
                  <a href="mailto:contact@diginova.ch" class="text-white hover:text-accent transition-colors duration-300">
                    contact@diginova.ch
                  </a>
                </div>
                <div class="flex items-center space-x-3">
                  <div class="w-10 h-10 bg-accent rounded-full flex items-center justify-center">
                    <i class="fas fa-map-marker-alt text-dark"></i>
                  </div>
                  <span class="text-white/80">Switzerland</span>
                </div>
              </div>
            </div>
            
            <!-- Navigation Columns -->
            <div>
              <h6 class="text-white font-semibold text-lg mb-6 font-heading">Company</h6>
              <ul class="space-y-3">
                <li>
                  <router-link to="/projects" class="text-white/70 hover:text-accent transition-colors duration-300 flex items-center group">
                    <span class="w-0 group-hover:w-2 h-0.5 bg-accent transition-all duration-300 mr-0 group-hover:mr-3"></span>
                    Projects
                  </router-link>
                </li>
                <li>
                  <router-link to="/about" class="text-white/70 hover:text-accent transition-colors duration-300 flex items-center group">
                    <span class="w-0 group-hover:w-2 h-0.5 bg-accent transition-all duration-300 mr-0 group-hover:mr-3"></span>
                    About
                  </router-link>
                </li>
                <li>
                  <router-link to="/contact" class="text-white/70 hover:text-accent transition-colors duration-300 flex items-center group">
                    <span class="w-0 group-hover:w-2 h-0.5 bg-accent transition-all duration-300 mr-0 group-hover:mr-3"></span>
                    Contact
                  </router-link>
                </li>
              </ul>
            </div>
            
            <div>
              <h6 class="text-white font-semibold text-lg mb-6 font-heading">Legal</h6>
              <ul class="space-y-3">
                <li>
                  <router-link to="/faq" class="text-white/70 hover:text-accent transition-colors duration-300 flex items-center group">
                    <span class="w-0 group-hover:w-2 h-0.5 bg-accent transition-all duration-300 mr-0 group-hover:mr-3"></span>
                    FAQ
                  </router-link>
                </li>
                <li>
                  <router-link to="/terms" class="text-white/70 hover:text-accent transition-colors duration-300 flex items-center group">
                    <span class="w-0 group-hover:w-2 h-0.5 bg-accent transition-all duration-300 mr-0 group-hover:mr-3"></span>
                    Terms of Use
                  </router-link>
                </li>
                <li>
                  <router-link to="/privacy" class="text-white/70 hover:text-accent transition-colors duration-300 flex items-center group">
                    <span class="w-0 group-hover:w-2 h-0.5 bg-accent transition-all duration-300 mr-0 group-hover:mr-3"></span>
                    Privacy Policy
                  </router-link>
                </li>
              </ul>
            </div>
          </div>
          
          <!-- Social Media & CTA Section -->
          <div class="border-t border-white/20 pt-12 mb-12">
            <div class="flex flex-col lg:flex-row items-center justify-between space-y-8 lg:space-y-0">
              <div class="text-center lg:text-left">
                <h3 class="text-2xl font-bold text-white mb-2">Ready to start your project?</h3>
                <p class="text-white/70">Let's create something amazing together.</p>
              </div>
              <div class="flex flex-col sm:flex-row items-center space-y-4 sm:space-y-0 sm:space-x-6">
                <!-- Social Links -->
                <div class="flex space-x-4">
                  <a 
                    href="https://www.instagram.com/diginova_ch" 
                    target="_blank"
                    class="social-link w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 group relative overflow-hidden"
                  >
                    <div class="absolute inset-0 bg-white/10 rounded-full transition-all duration-300 group-hover:bg-accent group-hover:scale-110"></div>
                    <i class="fab fa-instagram text-white group-hover:text-dark transition-colors relative z-10"></i>
                  </a>
                  <a 
                    href="https://www.facebook.com/diginova.ch" 
                    target="_blank"
                    class="social-link w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 group relative overflow-hidden"
                  >
                    <div class="absolute inset-0 bg-white/10 rounded-full transition-all duration-300 group-hover:bg-accent group-hover:scale-110"></div>
                    <i class="fab fa-facebook-f text-white group-hover:text-dark transition-colors relative z-10"></i>
                  </a>
                  <a 
                    href="#" 
                    target="_blank"
                    class="social-link w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 group relative overflow-hidden"
                  >
                    <div class="absolute inset-0 bg-white/10 rounded-full transition-all duration-300 group-hover:bg-accent group-hover:scale-110"></div>
                    <i class="fab fa-linkedin-in text-white group-hover:text-dark transition-colors relative z-10"></i>
                  </a>
                </div>
                <!-- CTA Button -->
                <router-link 
                  to="/contact" 
                  class="!bg-accent hover:bg-accent/90 text-dark font-semibold px-8 py-3 rounded-full transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl"
                >
                  Get Started
                </router-link>
              </div>
            </div>
          </div>
          
          <!-- Bottom Section -->
          <div class="border-t border-white/20 pt-8">
            <div class="justify-between space-y-4 md:space-y-0">
              <p class="text-white/60 text-sm text-right">
                Copyright &copy; {{ yearDate }} DIGINOVA. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </div>
    </footer>
    <!-- end Footer -->
</template>